import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Building2, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenants } from "@/hooks/useTenants";
import { PageHeader } from "@/components/kit/PageHeader";
import { Toolbar } from "@/components/kit/Toolbar";
import { StatCard } from "@/components/kit/StatCard";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtNumber, fmtDate } from "@/lib/format";
import { Building, Users as UsersIcon, DollarSign } from "lucide-react";
import { OrganizationDrawer } from "@/drawers/OrganizationDrawer";

export default function Organizations() {
  const { impersonate } = useAuth();
  const { data, isLoading } = useTenants();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const items = data?.items || [];

  const totalUsers = items.reduce((s, t) => s + (t.users_count || 0), 0);
  const totalRevenue = items.reduce((s, t) => s + (t.revenue || 0), 0);

  const openTenant = (t) => navigate(`/organizations/${t.internalId}`);
  const loginAs = async (t) => {
    try { await impersonate(t.internalId, "tenant_admin"); toast.success(`Logged in as ${t.name} admin`); navigate("/"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to impersonate"); }
  };

  const columns = [
    { key: "publicId", label: "ID", mono: true, render: (r) => <span className="text-muted-foreground">#{r.publicId}</span> },
    { key: "name", label: "Organization", render: (r) => (
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.06]"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /></span>
        <div>
          <div className="font-medium text-foreground">{r.name}</div>
          <div className="font-mono text-xs text-muted-foreground">{r.slug}</div>
        </div>
      </div>
    ) },
    { key: "owner", label: "Owner / Admin", render: (r) => r.owner ? <div><div className="text-foreground/90">{r.owner.name}</div><div className="text-xs text-muted-foreground">{r.owner.email}</div></div> : "—" },
    { key: "billing_type", label: "Plan", sortable: true, render: (r) => <span className="rounded bg-white/[0.05] px-2 py-0.5 text-xs capitalize text-foreground/90">{r.sub_plan || "—"}<span className={cn("ml-1.5", r.billing_type === "trial" ? "text-[#EAB308]" : r.billing_type === "paid" ? "text-[#22C55E]" : "text-muted-foreground")}>· {r.billing_type}</span></span> },
    { key: "sub_expires", label: "Expires", sortable: true, sortValue: (r) => r.sub_expires || "", render: (r) => r.sub_expires ? <span className={cn(r.sub_days_left <= 7 && "text-[#EAB308]")}>{fmtDate(r.sub_expires)}</span> : "—" },
    { key: "portal_url", label: "Portal URL", render: (r) => <code className="text-xs text-muted-foreground">{(r.portal_url || "").replace("https://", "")}</code> },
    { key: "domains_count", label: "Domains", align: "right", mono: true, render: (r) => fmtNumber(r.domains_count) },
    { key: "revenue", label: "Revenue", align: "right", mono: true, sortable: true, sortValue: (r) => r.revenue, exportValue: (r) => r.revenue, render: (r) => <span className="text-[#22C55E]">{fmtMoney(r.revenue)}</span> },
    { key: "last_activity", label: "Last activity", sortValue: (r) => r.last_activity || "", render: (r) => r.last_activity ? fmtDate(r.last_activity) : "—" },
    { key: "platform_version", label: "Version", render: (r) => <span className="text-xs text-muted-foreground">{r.platform_version}</span> },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
  ];

  const filters = [{ key: "billing_type", label: "Plan", getValue: (r) => r.billing_type, options: [{ value: "trial", label: "Trial" }, { value: "paid", label: "Paid" }, { value: "expired", label: "Expired" }] },
    { key: "status", label: "Status", getValue: (r) => r.status, options: [{ value: "active", label: "Active" }, { value: "suspended", label: "Suspended" }] }];

  const rowActions = (row) => [
    { label: "Open organization", icon: Eye, onClick: () => openTenant(row), testId: `view-tenant-${row.slug}` },
    { label: "Login as Tenant Admin", icon: LogIn, onClick: () => loginAs(row), testId: `impersonate-tenant-${row.slug}` },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Organizations" subtitle="All tenants on the platform. Open a tenant to view & manage it, or log in as its admin." />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Tenants" value={fmtNumber(items.length)} icon={Building} testId="stat-tenants" />
        <StatCard label="Total Users" value={fmtNumber(totalUsers)} icon={UsersIcon} />
        <StatCard label="Total Revenue" value={fmtMoney(totalRevenue)} icon={DollarSign} accent="#22C55E" />
      </div>
      <Toolbar onAdd={() => setDrawer({ open: true, item: null })} addLabel="Add Organization" addTestId="add-organization-btn" />
      <DataTablePro testId="tenants-table" columns={columns} rows={items} loading={isLoading} filters={filters}
        searchKeys={["name", "slug"]} exportName="organizations"
        onRowClick={openTenant} rowActions={rowActions} emptyText="No tenants found." />
      <OrganizationDrawer open={drawer.open} item={drawer.item} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} />
    </div>
  );
}

